import { useState, useEffect, useRef } from 'react';

/**
 * Agent thought templates organized by agent type/keywords
 */
const TOOL_CALL_TEMPLATES = {
  shift: [
    'Retrieving current shift summary',
    'Loading operator activity logs',
    'Calculating shift efficiency metrics',
    'Analyzing production output data',
    'Comparing actual vs planned targets',
    'Generating shift performance report',
    'Reviewing handoff notes',
    'Evaluating crew performance',
  ],
  review: [
    'Loading daily review data',
    'Analyzing 7-day trend patterns',
    'Comparing against historical baseline',
    'Running variance analysis',
    'Generating performance insights',
    'Scanning for anomalies',
  ],
  quality: [
    'Counting defects by production line',
    'Running SPC analysis',
    'Checking tolerance limits',
    'Fetching inspection results',
    'Analyzing defect trend patterns',
    'Calculating quality score',
    'Running Pareto analysis',
    'Checking calibration status',
  ],
  inspection: [
    'Loading inspection queue',
    'Fetching latest inspection results',
    'Running visual inspection check',
    'Analyzing measurement data',
    'Generating inspection report',
    'Checking compliance status',
  ],
  maintenance: [
    'Checking machine health status',
    'Running failure prediction model',
    'Loading maintenance schedule',
    'Analyzing vibration data',
    'Checking spare parts inventory',
    'Calculating MTBF/MTTR metrics',
    'Running predictive maintenance model',
    'Checking lubrication schedule',
  ],
  production: [
    'Loading production schedule',
    'Checking WIP inventory levels',
    'Optimizing production routing',
    'Detecting production bottlenecks',
    'Fetching order status updates',
    'Evaluating capacity utilization',
    'Checking material availability',
    'Optimizing production sequence',
  ],
  planning: [
    'Loading demand forecast data',
    'Running capacity planning model',
    'Analyzing resource allocation',
    'Fetching supplier lead times',
    'Evaluating schedule feasibility',
    'Generating production plan',
  ],
  energy: [
    'Loading energy consumption data',
    'Analyzing peak demand patterns',
    'Running efficiency analysis',
    'Fetching current utility rates',
    'Calculating carbon footprint',
    'Checking power factor',
  ],
  safety: [
    'Loading safety incident reports',
    'Running hazard assessment',
    'Checking PPE compliance',
    'Analyzing near-miss reports',
    'Checking safety training status',
    'Evaluating ergonomic risks',
  ],
  inventory: [
    'Loading stock levels',
    'Running reorder analysis',
    'Checking cycle count variance',
    'Analyzing inventory turnover',
    'Tracking incoming shipments',
    'Identifying dead stock items',
  ],
  default: [
    'Analyzing live data stream',
    'Fetching current metrics',
    'Generating insights',
    'Detecting trend patterns',
    'Evaluating performance score',
    'Checking alert thresholds',
    'Running correlation analysis',
    'Generating summary report',
  ],
};

/**
 * Get relevant tool calls based on agent configuration.
 * Uses custom simulatedThoughts if defined, otherwise falls back to keyword-based templates.
 * 
 * @param {Object} agent - Agent configuration object
 * @param {string} agent.name - Agent display name
 * @param {string[]} agent.simulatedThoughts - Optional custom thoughts array
 * @returns {string[]} - Array of thought strings to use
 */
const getToolCallsForAgent = (agent) => {
  // If agent has custom simulated thoughts defined, use those
  if (agent?.simulatedThoughts && Array.isArray(agent.simulatedThoughts) && agent.simulatedThoughts.length > 0) {
    return agent.simulatedThoughts;
  }
  
  // Fall back to keyword-based templates
  const agentName = agent?.name;
  if (!agentName) return TOOL_CALL_TEMPLATES.default;
  
  const name = agentName.toLowerCase();
  const allCalls = [];
  
  // Check each keyword and add matching tool calls
  Object.keys(TOOL_CALL_TEMPLATES).forEach((keyword) => {
    if (keyword !== 'default' && name.includes(keyword)) {
      allCalls.push(...TOOL_CALL_TEMPLATES[keyword]);
    }
  });
  
  // If no specific matches, use default
  if (allCalls.length === 0) {
    allCalls.push(...TOOL_CALL_TEMPLATES.default);
  }
  
  return allCalls;
};

/**
 * Hook for simulating agent behavior with realistic tool call thoughts.
 * 
 * @param {Object[]} agents - Array of agent configurations
 * @param {string} agents[].id - Unique agent ID
 * @param {string} agents[].name - Agent display name
 * @param {string} agents[].topicBase - Base topic path
 * @returns {Object} - { agentStates: { [agentId]: { thoughts, status, isHighlighted, isConnected } } }
 */
const useSimulatedAgents = (agents = []) => {
  const [agentStates, setAgentStates] = useState({});
  const timersRef = useRef({});
  const mountedRef = useRef(true);
  const initializedRef = useRef(false);

  // Serialize agents to detect real changes (not just reference changes)
  // Include simulatedThoughts so changes to thoughts trigger re-initialization
  const agentsKey = JSON.stringify(agents.map(a => ({ id: a.id, name: a.name, simulatedThoughts: a.simulatedThoughts })));

  useEffect(() => {
    mountedRef.current = true;
    
    // Don't run if no agents
    if (!agents || agents.length === 0) {
      setAgentStates({});
      return;
    }

    // Prevent double initialization in strict mode
    if (initializedRef.current) {
      return;
    }
    initializedRef.current = true;

    // Initialize all agent states
    const initialStates = {};
    agents.forEach((agent) => {
      initialStates[agent.id] = {
        thoughts: [],
        status: 'idle',
        isHighlighted: false,
        isConnected: true,
      };
    });
    setAgentStates(initialStates);

    // Function to run a single agent cycle
    const runAgentCycle = (agent) => {
      if (!mountedRef.current) return;
      
      const toolCalls = getToolCallsForAgent(agent);
      const hasCustomThoughts = agent?.simulatedThoughts && Array.isArray(agent.simulatedThoughts) && agent.simulatedThoughts.length > 0;
      
      let selectedCalls;
      if (hasCustomThoughts) {
        // Use custom thoughts in order (all of them)
        selectedCalls = [...toolCalls];
      } else {
        // For default templates, shuffle and select a random subset
        const numThoughts = 3 + Math.floor(Math.random() * 4); // 3-6 thoughts
        const shuffled = [...toolCalls].sort(() => Math.random() - 0.5);
        selectedCalls = shuffled.slice(0, numThoughts);
      }

      // Start thinking
      setAgentStates((prev) => ({
        ...prev,
        [agent.id]: {
          ...prev[agent.id],
          thoughts: [],
          status: 'thinking',
          isHighlighted: false,
        },
      }));

      // Emit thoughts one by one
      let thoughtIndex = 0;
      
      const emitThought = () => {
        if (!mountedRef.current) return;
        
        if (thoughtIndex < selectedCalls.length) {
          const thought = selectedCalls[thoughtIndex];
          
          setAgentStates((prev) => ({
            ...prev,
            [agent.id]: {
              ...prev[agent.id],
              thoughts: [...(prev[agent.id]?.thoughts || []), thought],
            },
          }));
          thoughtIndex++;
          
          // Schedule next thought (1-2 seconds)
          const delay = 1000 + Math.random() * 1000;
          timersRef.current[`${agent.id}_thought`] = setTimeout(emitThought, delay);
        } else {
          // Done thinking
          setAgentStates((prev) => ({
            ...prev,
            [agent.id]: {
              ...prev[agent.id],
              status: 'done',
              isHighlighted: true,
            },
          }));

          // Remove highlight after 3 seconds
          timersRef.current[`${agent.id}_highlight`] = setTimeout(() => {
            if (!mountedRef.current) return;
            setAgentStates((prev) => ({
              ...prev,
              [agent.id]: {
                ...prev[agent.id],
                isHighlighted: false,
              },
            }));
          }, 3000);

          // Return to idle and schedule next cycle (3-5 seconds after done)
          const idleDelay = 3000 + Math.random() * 2000;
          timersRef.current[`${agent.id}_idle`] = setTimeout(() => {
            if (!mountedRef.current) return;
            
            setAgentStates((prev) => ({
              ...prev,
              [agent.id]: {
                ...prev[agent.id],
                status: 'idle',
                thoughts: [],
              },
            }));

            // Schedule next cycle (5-15 seconds)
            const nextCycleDelay = 5000 + Math.random() * 10000;
            timersRef.current[`${agent.id}_cycle`] = setTimeout(() => {
              runAgentCycle(agent);
            }, nextCycleDelay);
          }, idleDelay);
        }
      };

      // Start emitting thoughts after a brief delay
      timersRef.current[`${agent.id}_start`] = setTimeout(emitThought, 500);
    };

    // Start each agent with a staggered delay
    agents.forEach((agent, index) => {
      const initialDelay = 1000 + index * 2000 + Math.random() * 2000; // Stagger starts
      timersRef.current[`${agent.id}_init`] = setTimeout(() => {
        runAgentCycle(agent);
      }, initialDelay);
    });

    // Cleanup on unmount
    return () => {
      mountedRef.current = false;
      initializedRef.current = false;
      Object.values(timersRef.current).forEach((timer) => clearTimeout(timer));
      timersRef.current = {};
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentsKey]);

  return { agentStates };
};

export default useSimulatedAgents;
